// frontend\src\components\analytics\useTableSort.ts
// Lightweight client-side table sorting shared by the analytics tables.
// Mirrors the main Stock table's behaviour: click a header to sort, click
// again to flip direction, with an ↑/↓ indicator on the active column.
import { useMemo, useState } from 'react';

export type SortOrder = 'asc' | 'desc';

export interface SortConfig<K extends string> {
  key: K;
  order: SortOrder;
}

// Returns a comparable primitive for a row/column; null/undefined sort last.
type ValueGetter<T, K extends string> = (row: T, key: K) => number | string | null | undefined;

export function useTableSort<T, K extends string>(
  rows: T[],
  getValue: ValueGetter<T, K>,
  initial: SortConfig<K> | null = null
) {
  const [sortConfig, setSortConfig] = useState<SortConfig<K> | null>(initial);

  const handleSort = (key: K) => {
    setSortConfig((prev) => {
      if (prev && prev.key === key) {
        return { key, order: prev.order === 'asc' ? 'desc' : 'asc' };
      }
      return { key, order: 'asc' };
    });
  };

  const sortedRows = useMemo(() => {
    if (!sortConfig) return rows;
    const { key, order } = sortConfig;
    const dir = order === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = getValue(a, key);
      const bv = getValue(b, key);
      // Nulls always sort to the bottom regardless of direction.
      const aNull = av === null || av === undefined;
      const bNull = bv === null || bv === undefined;
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, sortConfig, getValue]);

  // Indicator string to append to a header label.
  const indicator = (key: K) =>
    sortConfig && sortConfig.key === key ? (sortConfig.order === 'asc' ? ' ↑' : ' ↓') : '';

  return { sortedRows, sortConfig, handleSort, indicator };
}
